import { tokens } from '@neo4j-ndl/base';
import {
  Banner,
  Button,
  Divider,
  Drawer,
  LoadingSpinner,
  TextLink,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@neo4j-ndl/react';
import { ACTION, consoleApi, useActiveOrg, usePermissions } from '@nx/state';
import type {
  InstanceSeamlessConnectionEnabled,
  OrganizationSeamlessProfile,
  ProjectDefaultSeamlessConnectionEnabled,
} from '@nx/state';
import { Page, PageTitle } from '@nx/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { DeactivateSeamlessConnectionModal } from './confirm-deactivate-modal';
import {
  NewProjectSeamlessConnectionSettings,
  ProjectSeamlessConnectionSettings,
} from './project-seamless-connection-settings';

interface Props {
  data: OrganizationSeamlessProfile;
  state: SeamlessConfigState;
  onClose: () => void;
  onSubmit: () => void;
  updateState: ({
    instances,
    projects,
  }: {
    instances?: InstanceSeamlessConnectionEnabled[];
    projects?: ProjectDefaultSeamlessConnectionEnabled[];
  }) => void;
  fetchIsLoading: boolean;
  submitIsLoading: boolean;
  canSubmit: boolean;
}

export const SeamlessConnectionSettingsDrawer = ({
  data,
  state,
  onClose,
  onSubmit,
  updateState,
  fetchIsLoading,
  submitIsLoading,
  canSubmit,
}: Props) => {
  const { breakpoints } = tokens;
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.sm})`);

  const defaultWidth = useMemo(() => {
    if (isMobile) {
      return window.innerWidth;
    }
    return 720;
  }, [isMobile]);

  return (
    <Drawer
      isExpanded
      onExpandedChange={onClose}
      position="right"
      className="h-full !border-l"
      isResizeable
      resizeableProps={{
        defaultSize: { height: '100%', width: defaultWidth },
        minWidth: defaultWidth,
      }}
      isCloseable
    >
      <div className="flex h-full flex-col gap-4">
        <div className="mx-6 mb-1 flex flex-col gap-4">
          <h3>Tool authentication with Aura user</h3>
          <p className="text-neutral-text-weaker">
            Instances without this setting will require{' '}
            <Tooltip type="simple">
              <Tooltip.Trigger className="underline decoration-dotted">basic authentication</Tooltip.Trigger>
              <Tooltip.Content>Users are required to supply a database username and password</Tooltip.Content>
            </Tooltip>{' '}
            to connect to tools.
          </p>
          <Banner hasIcon type="warning">
            SSO Users: This setting will allow any project user with a{' '}
            <TextLink type="external" href="https://neo4j.com/docs/aura/security/tool-auth/">
              permitted console role
            </TextLink>{' '}
            to access instances via graph tools, in addition to users authenticating to instances via SSO.
          </Banner>
        </div>
        <div className="grow overflow-auto">
          {fetchIsLoading ? (
            <LoadingSpinner size="large" className="mx-auto" />
          ) : (
            <div className="flex flex-col gap-6 px-6">
              {data.projects.map((project) => (
                <ProjectSeamlessConnectionSettings
                  key={project.id}
                  projectSeamlessProfile={project}
                  state={state}
                  updateState={updateState}
                  className="bg-neutral-bg-default rounded-lg "
                />
              ))}
            </div>
          )}
        </div>

        <div className="mb-6 mr-6 mt-2 flex flex-row justify-end gap-4">
          <Button
            onClick={onClose}
            isDisabled={submitIsLoading || fetchIsLoading}
            color="neutral"
            fill="outlined"
            htmlAttributes={{
              'data-testid': 'close-seamless-connection-settings-button',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            isDisabled={fetchIsLoading || !canSubmit}
            isLoading={submitIsLoading}
            htmlAttributes={{
              'data-testid': 'submit-seamless-connection-settings-button',
            }}
          >
            Apply changes
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export interface SeamlessConfigState {
  projects: Record<string, boolean>;
  instances: Record<string, boolean>;
}

export const SeamlessConnectionSettings = () => {
  const activeOrg = useActiveOrg();
  const navigate = useNavigate();
  const { isAllowed } = usePermissions();
  const [showDeactivateSeamlessConnectionModal, setShowDeactivateSeamlessConnectionModal] = useState<boolean>(false);

  const allowUpdateSeamless = isAllowed(ACTION.UPDATE, `organizations/${activeOrg.id}/seamless`);

  const [updateOrganizationSeamlessProfile, updateOrgSeamlessProfileRes] =
    consoleApi.useUpdateOrganizationSeamlessProfileMutation();

  const orgSeamlessProfileRes = consoleApi.useGetOrganizationSeamlessProfileQuery(activeOrg.id, {
    skip: !activeOrg.capabilities.seamless_connection || !allowUpdateSeamless,
  });

  const seamlessConnectionSetup =
    orgSeamlessProfileRes.data?.projects.some(
      (project) =>
        project.defaultSeamlessConnectionEnabled ||
        project.instances.some((instance) => instance.seamlessConnectionEnabled),
    ) ?? false;

  const handleDeactivateSeamlessConnection = () => {
    const deactivate = async () => {
      await updateOrganizationSeamlessProfile({
        organizationId: activeOrg.id,
        organization: { seamlessConnectionEnabled: false },
      });
      setShowDeactivateSeamlessConnectionModal(false);
      await navigate('../security');
    };

    void deactivate();
  };

  return (
    <Page>
      <PageTitle
        title="Tool authentication"
        back={{
          label: 'Back to security',
          toUrl: '../security',
        }}
      />

      <div className="bg-neutral-bg-weak border-neutral-border-weak flex flex-col gap-3 rounded-lg border py-3">
        <Banner hasIcon type="warning" className="mx-3">
          SSO Users: Turning this setting on for instances will override settings implemented with your Identity
          Provider (IdP).
        </Banner>

        <Divider />
        <div className="mx-6 my-3 flex flex-row items-center gap-3">
          <div className="flex flex-col gap-1">
            <Typography variant="label">
              Allow tools to connect with permissions from the user's project role.
            </Typography>
            <Typography variant="body-medium">
              Instances without this setting will require{' '}
              <Tooltip type="simple">
                <Tooltip.Trigger className="underline decoration-dotted">basic authentication</Tooltip.Trigger>
                <Tooltip.Content>Users are required to supply a database username and password</Tooltip.Content>
              </Tooltip>{' '}
              to connect to tools.
            </Typography>
          </div>
          {seamlessConnectionSetup && (
            <Button
              color="danger"
              fill="outlined"
              className="ml-auto"
              onClick={() => setShowDeactivateSeamlessConnectionModal(true)}
              isLoading={updateOrgSeamlessProfileRes.isLoading}
            >
              Deactivate
            </Button>
          )}
        </div>
      </div>

      <div className="grow overflow-auto">
        {orgSeamlessProfileRes.isLoading ? (
          <LoadingSpinner size="large" className="mx-auto" />
        ) : (
          <div className="flex flex-col gap-6 ">
            {orgSeamlessProfileRes.data?.projects.map((project) => (
              <NewProjectSeamlessConnectionSettings key={project.id} projectSeamlessProfile={project} />
            ))}
          </div>
        )}
      </div>

      {showDeactivateSeamlessConnectionModal && (
        <DeactivateSeamlessConnectionModal
          onClose={() => setShowDeactivateSeamlessConnectionModal(false)}
          onConfirm={handleDeactivateSeamlessConnection}
          isLoading={updateOrgSeamlessProfileRes.isLoading}
        />
      )}
    </Page>
  );
};
